import { BroadcastChannelType, BroadcastEventType } from '@/types';
import { useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
const throttleBroadcastMillis = 5000;
const tabId = uuidv4();
export const useInactivityTimeout = (options = {}) => {
    const { onTimeout, timeoutMinutes = 15 } = options;
    const timeoutRef = useRef();
    const channelRef = useRef(null);
    const tabIdRef = useRef(tabId);
    const onTimeoutRef = useRef(onTimeout);
    // Ensure timeout is at least the minimum (1 minute)
    const resolvedTimeoutMinutes = Math.max(1, timeoutMinutes);
    // Convert minutes to milliseconds
    const resolvedTimeoutMillis = resolvedTimeoutMinutes * 60 * 1000;
    const broadcastEvent = useCallback((type) => {
        var _a, _b;
        const payload = {
            sourceId: (_a = tabIdRef.current) !== null && _a !== void 0 ? _a : '',
            timestamp: Date.now(),
            type
        };
        try {
            (_b = channelRef.current) === null || _b === void 0 ? void 0 : _b.postMessage(payload);
        }
        catch (_c) {
            // Do nothing
        }
    }, []);
    const triggerLogout = useCallback(() => {
        broadcastEvent(BroadcastEventType.LOGOUT);
        if (onTimeoutRef.current) {
            onTimeoutRef.current();
        }
    }, [broadcastEvent, onTimeout]);
    const resetTimer = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            triggerLogout();
        }, resolvedTimeoutMillis);
    }, [resolvedTimeoutMillis, triggerLogout]);
    useEffect(() => {
        var _a;
        channelRef.current = new BroadcastChannel(BroadcastChannelType.INACTIVITY_SYNC);
        let lastResetTime = Date.now();
        const handleActivity = (event) => {
            const now = Date.now();
            resetTimer();
            // Throttle resets to at most once every 5 seconds
            if (now - lastResetTime > throttleBroadcastMillis) {
                broadcastEvent(BroadcastEventType.ACTIVITY);
                lastResetTime = now;
            }
        };
        const handleSyncEvent = (event) => {
            // Ignore events from this tab
            if (!event || event.sourceId === tabIdRef.current)
                return;
            // Handle activity and logout events
            if (event.type === BroadcastEventType.ACTIVITY) {
                resetTimer();
            }
            // Logout event
            if (event.type === BroadcastEventType.LOGOUT) {
                if (onTimeout) {
                    onTimeout();
                }
            }
        };
        const handleChannelMessage = (message) => {
            const event = message.data;
            handleSyncEvent(event);
        };
        // Background tabs may freeze or heavily throttle timers (Safari/iOS apparently).
        // When the tab becomes visible again, reset the inactivity timer to avoid unexpected logouts.
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                resetTimer();
            }
        };
        activityEvents.forEach((event) => {
            document.addEventListener(event, handleActivity, true);
        });
        document.addEventListener('visibilitychange', handleVisibilityChange);
        (_a = channelRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('message', handleChannelMessage);
        resetTimer();
        // Remove event listeners and close channel
        return () => {
            var _a, _b;
            activityEvents.forEach((event) => {
                document.removeEventListener(event, handleActivity, true);
            });
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            (_a = channelRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('message', handleChannelMessage);
            (_b = channelRef.current) === null || _b === void 0 ? void 0 : _b.close();
            channelRef.current = null;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [broadcastEvent, resetTimer, triggerLogout]);
    const signalActivity = useCallback(() => {
        resetTimer();
        broadcastEvent(BroadcastEventType.ACTIVITY);
    }, [broadcastEvent, resetTimer]);
    return { signalActivity };
};
export default useInactivityTimeout;
